import SaveIcon from "@mui/icons-material/Save";
import { Button as UIButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import DeleteBin2LineIcon from "remixicon-react/DeleteBin2LineIcon";
import { useCallback, useRef, useMemo, useState } from "react";

import { Text } from "$components";
import { IProduct } from "$containers";
import { useStyles } from "../components/IntegratedOrders/IntegratedOrdersRecapTable";
import QuantityPicker from "$components/QuantityPicker/QuantityPicker";
import { EDBIntegratedOrders, OrderProduct } from "$types/EDBIntegratedOrders";
import useAuth from "$hooks/useAuth";
import { PreparationOrderStatus } from "$types";

export type CustomProduct = IProduct & OrderProduct;

export type ProductsBySuborder = {
  hasOnlyOneSuborder: boolean;
  orderId: number;
  orderReference: string;
  products: CustomProduct[];
  suborderId: number;
  suborderReference: string;
};

type Props = {
  integratedOrders: EDBIntegratedOrders;
  remainingIntegratedOrders?: EDBIntegratedOrders;
  edbProducts?: any[];
  opList?: any[];
  isEdbDisabled?: boolean;

  updateProducts?: () => void;
  handleClickProduct?: (product: IProduct) => void;
  setIntegratedOrders: (integratedOrders: EDBIntegratedOrders) => void;
  onDeleteIntegratedProduct?: (integratedOrders: EDBIntegratedOrders) => void;
};

const usePatchGetProductDetails = (integratedOrders: EDBIntegratedOrders) => {
  const allProductsBySuborder = useRef([]);

  useMemo(() => {
    const res: ProductsBySuborder[] = [];
    integratedOrders?.orderProducts?.forEach((orderProduct) => {
      const { suborderId, suborderProductId, order } = orderProduct;

      // Find suborder and suborderProduct to get product details
      const suborder = order.suborders.find((s) => s.id === suborderId);
      const suborderReference = suborder?.reference;

      const suborderProduct = suborder?.suborderProducts?.find(
        (sp) => sp.id === suborderProductId
      );

      // Build complete product from orderProduct + suborderProduct data
      // orderProduct has: quantity, initialQuantity, integrableQuantity
      // suborderProduct has: reference, name, unit
      const completeProduct = {
        ...orderProduct,
        _id: orderProduct.productReference || suborderProduct?.reference || "",
        reference:
          orderProduct.productReference || suborderProduct?.reference || "",
        designation: suborderProduct?.name || "",
        unit: suborderProduct?.unit || "",
        initialQuantity:
          orderProduct.initialQuantity == null
            ? orderProduct.quantity
            : orderProduct.initialQuantity,
      } as CustomProduct;

      // Fill / update the array
      const index = res.findIndex(
        (x) => x.orderId === order.id && x.suborderId === suborderId
      );
      if (index === -1) {
        res.push({
          hasOnlyOneSuborder:
            order.hasOnlyOneSuborder ?? order.suborders.length === 1,
          orderId: order.id,
          orderReference: order.reference,
          suborderId,
          suborderReference,
          products: [completeProduct],
        });
      } else {
        res[index].products.push(completeProduct);
      }
    });

    allProductsBySuborder.current = res;
  }, [integratedOrders]);

  return allProductsBySuborder.current;
};

const useEDBIntegratedOrdersTable = ({
  integratedOrders,
  remainingIntegratedOrders,
  opList,
  isEdbDisabled,
  updateProducts,
  handleClickProduct,
  setIntegratedOrders,
  onDeleteIntegratedProduct,
}: Props) => {
  const classes = useStyles();
  const { isClient } = useAuth();
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [openConfirmDeleteSuborder, setOpenConfirmDeleteSuborder] =
    useState(false);
  const [productToDelete, setProductToDelete] = useState<CustomProduct>(null);
  const [suborderToDelete, setSuborderToDelete] =
    useState<ProductsBySuborder>(null);
  const [editingSuborderReferences, setEditingSuborderReferences] = useState<
    string[]
  >([]);
  const allProductsBySuborder = usePatchGetProductDetails(integratedOrders);

  const isProductLocked = useCallback(
    (product: CustomProduct) => {
      const { suborderId, suborderProductId, order } = product;

      const remainingProduct = remainingIntegratedOrders?.orderProducts?.find(
        (op) =>
          op.suborderId === suborderId &&
          op.suborderProductId === suborderProductId &&
          op.order?.id === order?.id
      );

      const isFullyInTracking =
        remainingIntegratedOrders &&
        (!remainingProduct || remainingProduct.quantity <= 0);

      const productRef = product.reference;
      const isInActiveOP = opList?.some(
        (op) =>
          op.status !== PreparationOrderStatus.CANCELLED &&
          op.products?.some(
            (opProduct) =>
              opProduct.receptionReferenceId &&
              opProduct.relatedProduct?.reference === productRef
          )
      );

      return isFullyInTracking || isInActiveOP;
    },
    [remainingIntegratedOrders, opList]
  );

  const handleProductDelete = useCallback((product: CustomProduct) => {
    setOpenConfirmDelete(true);
    setProductToDelete(product);
  }, []);

  const handleOnQuantityChange = useCallback(
    (newQuantity: number, product: CustomProduct) => {
      const { orderProducts } = integratedOrders;
      const {
        suborderId,
        order: { id: orderId },
        suborderProductId,
      } = product;
      const newIntegratedOrders = {
        ...integratedOrders,
        orderProducts: orderProducts.map((orderProduct) => {
          if (
            orderProduct.suborderProductId === suborderProductId &&
            orderProduct.suborderId === suborderId &&
            orderProduct.order.id === orderId
          ) {
            return {
              ...orderProduct,
              quantity: newQuantity,
              initialQuantity:
                product.initialQuantity == null
                  ? product.quantity
                  : product.initialQuantity,
            };
          }
          return orderProduct;
        }),
      };
      setIntegratedOrders(newIntegratedOrders);
    },
    [integratedOrders, setIntegratedOrders]
  );

  const handleCancelDialog = useCallback(() => {
    setOpenConfirmDelete(false);
  }, []);

  const handleAllValidateDialog = () => {
    const {
      order: { id: orderId },
      suborderId,
      suborderProductId,
    } = productToDelete;
    setOpenConfirmDelete(false);
    setIntegratedOrders({
      ...integratedOrders,
      orderProducts: integratedOrders.orderProducts.filter(
        (orderProduct) =>
          orderProduct.order.id !== orderId ||
          orderProduct.suborderId !== suborderId ||
          orderProduct.suborderProductId !== suborderProductId
      ),
    });
  };

  const handleRecapValidateDialog = async () => {
    const {
      order: { id: orderId },
      suborderId,
      suborderProductId,
    } = productToDelete;
    const newIntegratedOrders = {
      ...integratedOrders,
      orderProducts: integratedOrders.orderProducts.filter(
        (orderProduct) =>
          orderProduct.order.id !== orderId ||
          orderProduct.suborderId !== suborderId ||
          orderProduct.suborderProductId !== suborderProductId
      ),
    };
    setIntegratedOrders(newIntegratedOrders);
    await onDeleteIntegratedProduct(newIntegratedOrders);
    setOpenConfirmDelete(false);
  };

  const isSuborderDeletable = useCallback(
    (productsBySuborder: ProductsBySuborder) => {
      return productsBySuborder.products.every(
        (product) => !isProductLocked(product)
      );
    },
    [isProductLocked]
  );

  const handleSuborderDelete = useCallback(
    (productsBySuborder: ProductsBySuborder) => {
      setSuborderToDelete(productsBySuborder);
      setOpenConfirmDeleteSuborder(true);
    },
    []
  );

  const handleCancelSuborderDialog = useCallback(() => {
    setOpenConfirmDeleteSuborder(false);
    setSuborderToDelete(null);
  }, []);

  const handleRecapValidateSuborderDialog = async () => {
    if (!suborderToDelete) return;

    const { orderId, suborderId } = suborderToDelete;
    const newIntegratedOrders = {
      ...integratedOrders,
      orderProducts: integratedOrders.orderProducts.filter(
        (orderProduct) =>
          orderProduct.order.id !== orderId ||
          orderProduct.suborderId !== suborderId
      ),
    };
    setIntegratedOrders(newIntegratedOrders);
    await onDeleteIntegratedProduct(newIntegratedOrders);
    setOpenConfirmDeleteSuborder(false);
    setSuborderToDelete(null);
  };

  const handleAllValidateSuborderDialog = () => {
    if (!suborderToDelete) return;

    const { orderId, suborderId } = suborderToDelete;
    setOpenConfirmDeleteSuborder(false);
    setIntegratedOrders({
      ...integratedOrders,
      orderProducts: integratedOrders.orderProducts.filter(
        (orderProduct) =>
          orderProduct.order.id !== orderId ||
          orderProduct.suborderId !== suborderId
      ),
    });
    setSuborderToDelete(null);
  };

  const getRecapThead = (productsBySuborder: ProductsBySuborder) => {
    const { hasOnlyOneSuborder, orderReference, suborderReference } =
      productsBySuborder;

    const isEditing = editingSuborderReferences.includes(suborderReference);
    const canDeleteSuborder = isSuborderDeletable(productsBySuborder);

    return [
      {
        id: "suborder-designation",
        label: hasOnlyOneSuborder
          ? `Commande n°${orderReference}`
          : `Sous-commande n°${suborderReference}`,
        width: 170,
      },
      {
        id: "désignation",
        label: "désignation",
        width: 340,
      },
      {
        id: "unite",
        label: "unité",
        width: 140,
      },
      {
        id: "qty",
        label: (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>Quantité</span>
            {/* not sure if i should use isAdmin, need to check with laurianne */}
            {!isClient() && (
              <UIButton
                style={{ marginLeft: 5, width: 35 }}
                className={classes.blueIcon}
                onClick={() => {
                  if (isEditing) {
                    updateProducts();
                    setEditingSuborderReferences(
                      editingSuborderReferences.filter(
                        (r) => r !== suborderReference
                      )
                    );
                  } else {
                    setEditingSuborderReferences([
                      ...editingSuborderReferences,
                      suborderReference,
                    ]);
                  }
                }}
                disabled={isEdbDisabled}
              >
                {isEditing ? <SaveIcon /> : <Edit2LineIcon />}
              </UIButton>
            )}
          </div>
        ),
        width: 50,
      },
      {
        id: "empty_column1",
        width: 200,
      },
      {
        id: "empty_column2",
        width: 100,
      },
      {
        id: "delete_suborder",
        label: !isClient() && (
          <UIButton
            variant="text"
            style={canDeleteSuborder && !isEdbDisabled ? {} : { opacity: 0.5 }}
            className={classes.deleteButton}
            onClick={() => handleSuborderDelete(productsBySuborder)}
            title={
              isEdbDisabled
                ? "EDB annulée : modification impossible"
                : canDeleteSuborder
                  ? "Supprimer toute la commande"
                  : "Impossible de supprimer : certains produits sont au suivi ou dans une OP"
            }
            disabled={!canDeleteSuborder || isEdbDisabled}
          >
            <DeleteIcon style={{ fontSize: 18 }} />
          </UIButton>
        ),
        width: 100,
      },
    ];
  };

  const getTheadAllProducts = (product: ProductsBySuborder) => {
    const { hasOnlyOneSuborder, orderReference, suborderReference } = product;
    return [
      {
        id: "suborder-designation",
        label: hasOnlyOneSuborder
          ? `Commande n°${orderReference}`
          : `Sous-commande n°${suborderReference}`,
        width: 500,
      },
      {
        id: "ref",
        label: "Référence",
        width: 250,
      },
      {
        id: "unit",
        label: "Unité",
        width: 150,
      },
      {
        id: "value",
        label: "Quantité",
        width: 120,
      },
      {
        id: "empty_column",
        label: "",
      },
      {
        id: "empty_column2",
        label: "",
      },
      {
        id: "button",
        label: "",
        width: 10,
      },
    ];
  };

  const renderAllProducts = (products: CustomProduct[]) => {
    return products.map((product) => {
      const { integrableQuantity } = product;
      return [
        {
          value: <Text type="important">{product.designation}</Text>,
          onClickAction: () => handleClickProduct(product),
        },
        {
          value: <Text>{product.reference || "-"}</Text>,
          onClickAction: () => handleClickProduct(product),
        },
        {
          value: product.unit,
          onClickAction: () => handleClickProduct(product),
        },
        {
          value: (
            <QuantityPicker
              product={product}
              minValue={1}
              maxValue={integrableQuantity}
              onQuantityChange={(quantity) =>
                handleOnQuantityChange(quantity, product)
              }
              quantity={product.quantity}
              size="medium"
            />
          ),
        },
        {},
        {},
        {
          value: (
            <UIButton
              variant="text"
              style={
                isProductLocked(product)
                  ? { opacity: 0.5, textTransform: "none" }
                  : { textTransform: "none" }
              }
              onClick={() => handleProductDelete(product)}
              className={classes.deleteButton}
              disabled={isProductLocked(product)}
            >
              <DeleteBin2LineIcon />
            </UIButton>
          ),
        },
      ];
    });
  };

  const renderRecapProducts = (productsBySuborder: ProductsBySuborder) => {
    const { products, suborderReference } = productsBySuborder;

    return products.map((product) => {
      const { designation, integrableQuantity, quantity, reference } = product;

      const isEditing =
        editingSuborderReferences.includes(suborderReference) && !isEdbDisabled;
      const isDeleteDisabled = isProductLocked(product) || isEdbDisabled;
      return [
        {
          value: <Text>{reference || "-"}</Text>,
        },
        {
          value: <Text type="important">{designation}</Text>,
        },
        {
          value: product.unit,
        },
        {
          value: isEditing ? (
            <QuantityPicker
              product={product}
              minValue={1}
              maxValue={integrableQuantity}
              onQuantityChange={(quantity) =>
                handleOnQuantityChange(quantity, product)
              }
              quantity={quantity}
              size="medium"
            />
          ) : (
            quantity
          ),
        },
        {},
        {},
        {
          value: !isClient() && (
            <UIButton
              variant="text"
              style={
                isDeleteDisabled
                  ? { opacity: 0.5, textTransform: "none" }
                  : { textTransform: "none" }
              }
              onClick={() => {
                handleProductDelete(product);
              }}
              disabled={isDeleteDisabled}
              className={classes.deleteButton}
            >
              <DeleteIcon className={classes.blueIcon} />
            </UIButton>
          ),
        },
      ];
    });
  };

  return {
    allProductsBySuborder,
    handleCancelDialog,
    handleOnQuantityChange,
    handleProductDelete,
    openConfirmDelete,
    productToDelete,
    setOpenConfirmDelete,
    getRecapThead,
    getTheadAllProducts,
    renderAllProducts,
    renderRecapProducts,
    handleAllValidateDialog,
    handleRecapValidateDialog,
    // Suppression de sous-commande
    openConfirmDeleteSuborder,
    suborderToDelete,
    handleSuborderDelete,
    handleCancelSuborderDialog,
    handleRecapValidateSuborderDialog,
    handleAllValidateSuborderDialog,
    isSuborderDeletable,
  };
};

export default useEDBIntegratedOrdersTable;
